import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const IconSun = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const IconMoon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

var PASSWORD_MIN_LENGTH = 8;

// Public page reached from the invitation email: the invitee sets their name
// and password, then signs in normally (first sign-in runs the TOTP setup).
function AcceptInvite({ darkMode, toggleDarkMode }) {
  var navigate = useNavigate();
  var [searchParams] = useSearchParams();
  var token = searchParams.get('token') || '';

  // status is 'checking' | 'valid' | 'invalid' | 'expired' | 'done'
  var [status, setStatus] = useState('checking');
  var [email, setEmail] = useState('');
  var [name, setName] = useState('');
  var [password, setPassword] = useState('');
  var [confirm, setConfirm] = useState('');
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);

  useEffect(function() {
    if (!token) {
      setStatus('invalid');
      return;
    }
    api.get('/invites/validate', { params: { token: token } })
      .then(function(res) {
        if (res.data.valid) {
          setEmail(res.data.email);
          setStatus('valid');
        } else {
          setStatus(res.data.reason === 'expired' ? 'expired' : 'invalid');
        }
      })
      .catch(function(err) {
        setStatus('invalid');
        setError(err.response?.data?.error || '');
      });
  }, [token]);

  useEffect(function() {
    if (status !== 'done') return undefined;
    var timer = setTimeout(function() { navigate('/login'); }, 2000);
    return function() { clearTimeout(timer); };
  }, [status, navigate]);

  var handleSubmit = async function(e) {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError('Password must be at least ' + PASSWORD_MIN_LENGTH + ' characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/invites/accept', { token: token, name: name.trim(), password: password });
      setStatus('done');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not complete registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  var unusable = status === 'invalid' || status === 'expired';

  return (
    <div className="console-auth">
      {/* ── Top bar ── */}
      <header className="console-topbar">
        <div className="console-brand">
          <div className="console-brand-mark"><IconShield /></div>
          <h2>Shaheen</h2>
        </div>
        <div className="console-channel">
          <span className="pulse-dot ok" />
          <span>Monitoring active</span>
        </div>
      </header>

      {/* ── Center panel ── */}
      <main className="console-center">
        <div className="console-panel rise" style={{ '--i': 0 }}>
          <div className="console-panel-head">
            <span className={'pulse-dot ' + (unusable ? 'bad' : 'ok')} aria-hidden="true" style={{ animation: 'none' }} />
            <h1>
              {status === 'checking' && 'Checking invitation'}
              {status === 'valid' && 'Accept invitation'}
              {unusable && 'Invitation unavailable'}
              {status === 'done' && 'Account created'}
            </h1>
          </div>

          <div className="console-panel-body">
            {status === 'checking' && (
              <p className="console-panel-sub">Verifying your invitation link…</p>
            )}

            {unusable && (
              <>
                <div className="alert alert-error">
                  {status === 'expired'
                    ? 'This invitation has expired. Ask your admin to resend it.'
                    : 'This invitation is invalid or has expired. Ask your admin to resend it.'}
                </div>
                <button type="button" className="btn btn-primary" onClick={function() { navigate('/login'); }}>
                  Go to Sign In
                </button>
              </>
            )}

            {status === 'done' && (
              <div className="alert alert-success">Account created! Redirecting to sign in…</div>
            )}

            {status === 'valid' && (
              <>
                <p className="console-panel-sub">
                  Set up your account. After this you'll sign in and secure it with an authenticator app.
                </p>

                {error && <div className="alert alert-error">{error}</div>}

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Email address</label>
                    <input type="email" value={email} readOnly disabled autoComplete="username" />
                  </div>

                  <div className="form-group">
                    <label>Full name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={function(e) { setName(e.target.value); }}
                      required
                      maxLength={255}
                      placeholder="Your name"
                      autoComplete="name"
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={function(e) { setPassword(e.target.value); }}
                      required
                      minLength={PASSWORD_MIN_LENGTH}
                      maxLength={128}
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                    <div className="form-hint">At least {PASSWORD_MIN_LENGTH} characters</div>
                  </div>

                  <div className="form-group">
                    <label>Confirm password</label>
                    <input
                      type="password"
                      value={confirm}
                      onChange={function(e) { setConfirm(e.target.value); }}
                      required
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Creating account…' : 'Create Account'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        <button className="auth-theme-btn" onClick={toggleDarkMode}>
          {darkMode ? <IconSun /> : <IconMoon />}
          {darkMode ? 'Switch to Day Shift' : 'Switch to Night Shift'}
        </button>
      </main>

      {/* ── Foot ── */}
      <footer className="console-foot">
        Uptime and DNS record monitoring
      </footer>
    </div>
  );
}

export default AcceptInvite;
